import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './ModalBase.css';

/**
 * ModalBase Component - Unified modal system for TaskManager feature
 *
 * Supports 4 variants:
 * - default: Standard modal layout
 * - form-grid: Grid-based form layout for TaskForm
 * - split-panel: Dual-panel layout for AdvancedSearch
 * - multi-section: Multiple section layout for Settings
 *
 * Supports 3 sizes: small, medium, large
 *
 * Usage:
 * <ModalBase footer={<ModalBase.Footer>content</ModalBase.Footer>}>
 *   <ModalBase.Content>body content</ModalBase.Content>
 * </ModalBase>
 */
const ModalBase = ({
  isOpen = false,
  onClose = null,
  size = 'medium', // 'small', 'medium', 'large'
  variant = 'default', // 'default', 'form-grid', 'split-panel', 'multi-section'
  title = '',
  subtitle = '',
  className = '',
  children,
  footer,
  ...props
}) => {
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Store the previously focused element when modal opens
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
    }
  }, [isOpen]);

  // Handle ESC key press
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && isOpen) {
      onClose();
    }
  }, [isOpen, onClose]);

  // Handle click outside modal
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Focus trap implementation
  const handleTabKey = useCallback((e) => {
    if (!modalRef.current) return;

    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    }
  }, []);

  // Keyboard event handler
  useEffect(() => {
    const handleKeyDownEvent = (e) => {
      if (e.key === 'Escape') {
        handleKeyDown(e);
      } else if (e.key === 'Tab') {
        handleTabKey(e);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDownEvent);
      return () => document.removeEventListener('keydown', handleKeyDownEvent);
    }
  }, [isOpen, handleKeyDown, handleTabKey]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Focus management
  useEffect(() => {
    if (isOpen && modalRef.current) {
      // Focus the modal container or first focusable element
      const firstFocusable = modalRef.current.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusable) {
        firstFocusable.focus();
      } else {
        modalRef.current.focus();
      }
    } else if (!isOpen && previousFocusRef.current) {
      // Restore focus to previously focused element
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      className={`modal-base-backdrop ${isOpen ? 'modal-base-backdrop--open' : ''}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div
        ref={modalRef}
        className={`modal-base modal-base--${size} modal-base--${variant} ${className}`}
        tabIndex={-1}
        {...props}
      >
        {(title || subtitle) && (
          <div className="modal-base__header">
            {title && (
              <h2 id="modal-title" className="modal-base__title">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="modal-base__subtitle">
                {subtitle}
              </p>
            )}
            <button
              type="button"
              className="modal-base__close-button"
              onClick={onClose}
              aria-label="Close modal"
            >
              ×
            </button>
          </div>
        )}

        <div className="modal-base__body">
          {children}
        </div>

        {footer && (
          <div className="modal-base__footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // Render modal in portal
  return createPortal(modalContent, document.body);
};

// Content sub-component
const Content = ({ children, className = '' }) => (
  <div className={`modal-base__content ${className}`}>
    {children}
  </div>
);

// Footer sub-component
const Footer = ({ children, className = '' }) => (
  <div className={`modal-base__footer ${className}`}>
    {children}
  </div>
);

// Section sub-component for Apple-style visual distinction
const Section = ({ children, title, className = '', compact = false }) => (
  <div className={`modal-base__section ${compact ? 'modal-base__section--compact' : ''} ${className}`}>
    {title && <h3 className="modal-base__section-title">{title}</h3>}
    <div className="modal-base__section-content">
      {children}
    </div>
  </div>
);

// Attach sub-components
ModalBase.Content = Content;
ModalBase.Footer = Footer;
ModalBase.Section = Section;

export default ModalBase;